/**
 * Copyright (c) Simam Digital.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
	collection,
	doc,
	getDoc,
	getDocs,
	runTransaction,
	serverTimestamp,
	setDoc,
} from 'firebase/firestore';

import { getDb } from './firebaseClient';

/**
 * Firestore access for the shared garden world.
 *
 * Data model (see SHARED_GARDEN_DESIGN.md):
 *   users/{uid}    -> { plotId, plotIndex, displayName, country, createdAt, lastSeen }
 *   plots/{plotId} -> { ownerUid, plotIndex, displayName, plants:[...], updatedAt }
 *   meta/plotCounter -> { next }   // atomic plot-index allocator
 */

/**
 * Get the user's plot, allocating a brand-new one on first visit.
 * @param {string} uid
 * @param {{ displayName?: string, country?: string }} profile
 * @returns {Promise<{ plotId: string, plotIndex: number }>}
 */
export async function ensurePlotForUser(uid, profile = {}) {
	const db = getDb();
	const userRef = doc(db, 'users', uid);
	const userSnap = await getDoc(userRef);

	if (userSnap.exists() && userSnap.data().plotId != null) {
		const data = userSnap.data();
		await setDoc(userRef, { lastSeen: serverTimestamp() }, { merge: true });
		return { plotId: data.plotId, plotIndex: data.plotIndex };
	}

	// Atomically grab the next free plot index.
	const plotIndex = await runTransaction(db, async (tx) => {
		const counterRef = doc(db, 'meta', 'plotCounter');
		const snap = await tx.get(counterRef);
		const next = snap.exists() ? snap.data().next || 0 : 0;
		tx.set(counterRef, { next: next + 1 }, { merge: true });
		return next;
	});

	const plotId = `plot_${plotIndex}`;
	const displayName = profile.displayName || 'Gardener';

	await setDoc(doc(db, 'plots', plotId), {
		ownerUid: uid,
		plotIndex,
		displayName,
		plants: [],
		updatedAt: serverTimestamp(),
	});

	await setDoc(
		userRef,
		{
			plotId,
			plotIndex,
			displayName,
			country: profile.country || 'Unknown',
			createdAt: serverTimestamp(),
			lastSeen: serverTimestamp(),
		},
		{ merge: true },
	);

	return { plotId, plotIndex };
}

/**
 * Load every plot in the world.
 * @returns {Promise<Array<{ id: string, ownerUid: string, plotIndex: number, displayName: string, plants: Array }>>}
 */
export async function loadAllPlots() {
	const db = getDb();
	const snap = await getDocs(collection(db, 'plots'));
	return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Persist the serialized plant list for a plot. The caller must own the plot
 * (enforced by Firestore security rules).
 * @param {string} plotId
 * @param {Array} plants - serialized SavableObject entities (existing save format)
 */
export async function savePlotPlants(plotId, plants) {
	const db = getDb();
	await setDoc(
		doc(db, 'plots', plotId),
		{ plants, updatedAt: serverTimestamp() },
		{ merge: true },
	);
}
